import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Clock, Download, ArrowUpDown } from "lucide-react";

export const AdminMetrics = () => {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['admin-metrics'],
    queryFn: async () => {
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString();
      
      const [
        { count: totalVisits },
        { count: uniqueUsers },
        { count: downloads },
        avgDurationResult
      ] = await Promise.all([
        supabase
          .from('user_tracking')
          .select('*', { count: 'exact', head: true })
          .eq('action', 'page_visit'),
        supabase
          .from('user_tracking')
          .select('user_email', { count: 'exact', head: true })
          .eq('action', 'page_visit')
          .gte('created_at', startOfDay),
        supabase
          .from('user_tracking')
          .select('*', { count: 'exact', head: true })
          .eq('action', 'download'),
        supabase
          .from('user_tracking')
          .select('duration')
          .eq('action', 'page_visit')
          .not('duration', 'is', null)
      ]);

      const avgDuration = avgDurationResult.data?.reduce((acc, curr) => acc + (curr.duration || 0), 0) || 0;
      const avgTimeSpent = avgDuration > 0 ? Math.round(avgDuration / (avgDurationResult.data?.length || 1)) : 0;

      return {
        totalVisits: totalVisits || 0,
        uniqueUsers: uniqueUsers || 0,
        downloads: downloads || 0,
        avgTimeSpent
      };
    }
  });

  if (isLoading) {
    return <div>Loading metrics...</div>;
  }

  const metricCards = [
    {
      title: "Total Page Visits",
      value: metrics?.totalVisits || 0,
      icon: Users,
      description: "Total number of page views"
    },
    {
      title: "Unique Users Today",
      value: metrics?.uniqueUsers || 0,
      icon: ArrowUpDown,
      description: "Distinct users today"
    },
    {
      title: "Average Time Spent",
      value: `${Math.floor((metrics?.avgTimeSpent || 0) / 60)}m ${(metrics?.avgTimeSpent || 0) % 60}s`,
      icon: Clock,
      description: "Average session duration"
    },
    {
      title: "Total Downloads",
      value: metrics?.downloads || 0,
      icon: Download,
      description: "Number of file downloads"
    }
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {metricCards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {card.title}
            </CardTitle>
            <card.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
            <p className="text-xs text-muted-foreground">
              {card.description}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};